import { useLocation, useNavigate, useParams } from "react-router";
import * as retreatsService from "@/services/retreats-service";
import * as tripPaymentsService from "@/services/trip-payments-service";
import * as tripsService from "@/services/trips-service";
import { useEffect, useState } from "react";
import IRetreatOverview from "@/models/retreats/iretreat-overview";
import IRoom from "@/models/rooms/iroom";
import { useModal } from "@/components/modals/templating/modal";
import { MouseEvent } from "react";
import Spinner from "@/components/spinner";
import FormatDate from "@/components/formatting/format-date";
import EditRetreat from "./edit";
import ITrip from "@/models/trips/itrip";
import IGuestTrip from "@/models/trips/iguest-trip";
import AddOrEditGuestAndTrip from "@/pages/guests/add-edit";

function ViewRetreat() {
    const params = useParams();
    const { state } = useLocation();

    const highlightedGuestId = state?.guestId;
    const highlightedTripId = state?.tripId;
    
    const [ isLoading, setIsLoading ] = useState<boolean>(true);
    const [ retreat, setRetreat ] = useState<IRetreatOverview>();
    const [ rooms, setRooms ] = useState<IRoom[]>();
    const [ missingPayments, setMissingPayments ] = useState<number[]>([]);

    const [ selectedGuestTrip, setSelectedGuestTrip ] = useState<IGuestTrip>({} as IGuestTrip);
    const [ draggedRoom, setDraggedRoom ] = useState<number>(-1);
    const [ draggedGuestTrip, setDraggedTrip ] = useState<IGuestTrip>();

    // Modals
    const EditRetreatModal = useModal(<EditRetreat id={retreat?.id} closeModal={(requiresUpdate: boolean) => closeEditRetreatModal(requiresUpdate)}/>);
    const EditGuestAndTripModal = useModal(<AddOrEditGuestAndTrip guestTrip={selectedGuestTrip} isAdding={false} closeModal={(requiresUpdate: boolean) => closeEditGuestModal(requiresUpdate)}/>);
    const AddGuestAndTripModal = useModal(<AddOrEditGuestAndTrip guestTrip={{retreatId: Number(params.id)} as IGuestTrip} isAdding={true} closeModal={(requiresUpdate: boolean) => closeAddGuestModal(requiresUpdate)}/>);
    
    const navigate = useNavigate();

    async function updateData() {
        try {
            const missingPayments = tripPaymentsService.getTripsWithMissingPaymentsForRetreat(params.id!);

            const [retreat, rooms] = await Promise.all([
                retreatsService.getRetreat(params.id!),
                tripsService.getRoomsWithTrips(params.id!)
            ]);

            if (!retreat || !rooms) {
                navigate('/notfound', { state: { message: "The was an issue loading the retreat, please check it exists, has valid rooms and try again." } });
            } 

            setRetreat(retreat);

            setRooms(rooms!.map(room => {
                const rooming = calculateRoomingCapacity(room);

                return {
                    ...room,
                    amountOfGuests: rooming[0],
                    availableCapacity: rooming[1]
                } as IRoom
            }))

            setMissingPayments(await missingPayments);

        } catch (e) {
            if (e instanceof Error && (e as Error).message.includes("Unauthorised")) {
                navigate("/login");
            } else {
                navigate('/notfound', { state: { message: "The was an issue loading the retreat." } });
            }
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            if (params?.id) {
                await updateData();
                
                setIsLoading(false);
            }
        }

        fetchData();
    }, []);

    function closeEditRetreatModal(requiresUpdate: boolean) {
        EditRetreatModal.closeModal();

        if (requiresUpdate) {
            updateData();
        }
    }

    function launchEditGuestModal(guestTrip: IGuestTrip) {
        setSelectedGuestTrip(guestTrip);
        EditGuestAndTripModal.openModal();
    }

    function closeEditGuestModal(requiresUpdate: boolean) {
        EditGuestAndTripModal.closeModal();

        if (requiresUpdate) {
            updateData();
        }
    }

    function closeAddGuestModal(requiresUpdate: boolean) {
        AddGuestAndTripModal.closeModal();

        if (requiresUpdate) {
            updateData();
        }
    }

    function calculateRoomingCapacity(room: IRoom): [number, number] {
        if (room.guestTrips.length === 0) {
            return [0, room.maxOccupancy];
        }

        const isSingleOccupancy = room.guestTrips.find(x => x.bookedRoomTypeId === 3) ? true : false;

        if (isSingleOccupancy) {
            return [1,1];
        }
        
        return [room.guestTrips.length, room.maxOccupancy];
    }
    
    function onDragEnterHandler(room: IRoom) {
        if (room.availableCapacity > room.amountOfGuests || room.id === 0) {
            setDraggedRoom(room.id);
        }
    }
    
    function onDragOverHandler(event: MouseEvent<HTMLDivElement>, room: IRoom) {
        if (room.availableCapacity > room.amountOfGuests || room.id === 0) {
            event.preventDefault();
        }
    }
    
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    async function onDropHandler(event: MouseEvent<HTMLDivElement>, id: number) {
        setDraggedRoom(-1);

        await tripsService.updateTrip({
            ...draggedGuestTrip,
            id: draggedGuestTrip?.tripId,
            roomId: id === 0 ? null : id
        } as ITrip)

        updateData();
    }
    
    return (
        <Spinner isLoading={isLoading}>
            
            {/* Back button */}
            <div className="cursor-pointer mb-10 md:mb-0" onClick={() => navigate(-1)}>
                <div className="invisible md:visible absolute top-12 left-5">
                    <i className="fa-3x far fa-caret-square-left text-white opacity-60"></i>
                </div>
                <div className="visible md:invisible absolute top-0 left-0 w-full bg-slate-800 py-1 pl-2">
                    <i className="far fa-caret-square-left text-white opacity-60"></i> View all retreats
                </div>
            </div>

            <div>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-thin text-center">{retreat?.venue}, {retreat?.country}</h1>
                <hr className="lg:w-2/3 mx-auto my-3" />
                <div className="flex justify-center text-xl mb-5">
                    {retreat?.instructor} <span className="px-4">|</span> {retreat?.occupancy}/{retreat?.maxOccupancy} <span className="px-4">|</span><span><FormatDate date={retreat?.startDate} /> - <FormatDate date={retreat?.endDate} includeYear={true} /></span> <span className="px-4">|</span><i className="fa fa-edit cursor-pointer" onClick={EditRetreatModal.openModal}></i>
                </div>

                <div className="lg:w-2/3 xl:w-1/2 mx-auto">
                    {!retreat?.manageRooming ? (
                        <div className="text-xl bg-br-blue-300 p-4 rounded-md">This retreat does not require rooming. If this is wrong, please update the retreat information on the edit screen.</div>
                    ) : (
                        <>
                        {rooms?.map(room => (
                            <div 
                                className="border-b border-gray-900 last:border-b-0" 
                                key={room.id} 
                                onDragEnter={() => onDragEnterHandler(room)}
                                onDragOver={(e) => onDragOverHandler(e, room)}
                                onDrop={(e) => onDropHandler(e, room.id)} 
                            >
                                {/* Rooms */}
                                <p className="bg-blue-800 text-lg py-2 px-3">
                                    {room.name} {room.id !== 0 ? `(${room.amountOfGuests}/${room.availableCapacity})` : ""}
                                    {room.roomTypeId === 0 && (
                                        <button className="bg-green-500 px-2 float-right" onClick={AddGuestAndTripModal.openModal}><i className="fas fa-plus" /></button>
                                    )}
                                </p>

                                {/* Guests */}
                                {room.guestTrips.length > 0 && room.guestTrips.map(guestTrip => (
                                    <div 
                                        className={`${highlightedGuestId === guestTrip.guestId || highlightedTripId === guestTrip.tripId ? "bg-amber-400" : "bg-br-blue-500"} hover:brightness-110 py-1 px-4 border-t border-white/40 border-dashed  duration-100 cursor-pointer`}
                                        key={guestTrip.tripId}
                                        draggable={true}
                                        onDragStart={() => setDraggedTrip(guestTrip)}
                                        onDragEnd={() => setDraggedRoom(-1)}
                                        onClick={() => launchEditGuestModal(guestTrip)}
                                    > 
                                        <div className="float-right">
                                            {missingPayments.find(x => x === guestTrip.tripId) && (
                                                <i className="fas fa-usd-circle" style={{color: "#fefb00"}} title="Missing payment"></i>
                                            )}
                                        </div>

                                        {guestTrip.isInstructor ? (
                                            <p>{retreat?.instructor}</p>
                                        ) : (
                                            <p>{guestTrip.firstName} {guestTrip.lastName} {guestTrip.isCancelled ? "[CANCELLED]" : ""}</p>
                                        )}
                                    </div>
                                ))}

                                { draggedRoom === room.id && <div className="bg-red-500 h-0.5"></div> }
                            </div>
                        ))}

                            {EditGuestAndTripModal.modalOpen && <EditGuestAndTripModal.ModalContent />}
                            {AddGuestAndTripModal.modalOpen && <AddGuestAndTripModal.ModalContent />}
                            </>
                        )}
                    </div> 

                    {EditRetreatModal.modalOpen && <EditRetreatModal.ModalContent />}
            </div>
        </Spinner>
    )
}

export default ViewRetreat;